import { useCallback, useEffect, useState } from 'react';
import {
  ChangeLog,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  usePrevious,
} from '@ui/src';
import copy from 'copy-to-clipboard';
import { useAppDispatch } from '@store';
import {
  FieldValues,
  UseFormClearErrors,
  UseFormSetError,
} from 'react-hook-form';
import {
  useAddNewApplicationCommentMutation,
  useCreateApplicationClientLockMutation,
  useCreateApplicationManualRiskScoreMutation,
  useCreateApplicationRiskScoreMutation,
  useLazyGetApplicationAmlUpdatingItemQuery,
  useLazyGetApplicationChangeLogsQuery,
  useLazyGetApplicationClientActiveLocksQuery,
  useLazyGetApplicationClientAuditLogsQuery,
  useLazyGetApplicationClientLockItemAuditLogsQuery,
  useLazyGetApplicationClientLockItemQuery,
  useLazyGetApplicationClientLocksQuery,
  useLazyGetApplicationRiskScoreQuery,
  useLazyGetApplicationsAMLScoresHistoryQuery,
  useLazyGetNewApplicationSystemLogsQuery,
  useResetApplicationClientPasswordMutation,
  useSetReviewEmployeeMutation,
  useSubmitApplicationMutation,
  useUpdateApplicationClientLockMutation,
  useUpdateApplicationClientMutation,
  useUpdateCOFQuestionnarieMutation,
  useUpdateCompanyOfficialApplicationMutation,
  useUpdateCompanyOfficialStatusMutation,
  useUpdateCorporateApplicationMutation,
  useUpdateCorporateApplicationStatusMutation,
  useUpdateCorporateQuestionnarieMutation,
  useUpdateIndividualApplicationStatusMutation,
  useUpdateInividualQuestionnarieMutation,
  useUpdateNewApplicationMutation,
} from '../../store/services';
import {
  NewApplicationCorporateInfo,
  NewApplicationListItem,
  NewApplicationStatus,
  ReviewStatus,
} from '../../types';
import getIndividualApplicationBody from '../../individual/helpers/get-individual-application-body';
import {
  CustomerScore,
  CustomerScoreUpdateStatus,
  RiskScoreRange,
} from '../../../common/types';
import getCofandIndividualQuestionnarieBody from '../../individual/helpers/get-qestionnarie-body';
import { COFAndIndividualQuestionnarieFormValues } from '../../../individual/types';
import getCorporateAppicationBody from '../../corporate/helpers/get-corporate-application-body';
import getCorporateQuestionnarieBody from '../../corporate/helpers/get-questionnarie-body';
import { CorporateQuestionnarieFormValues } from '../../../corporate/types';
import { clearAmlChangeLogs } from '../../store/slices';
import { Employee } from '../../../../store/dictionaries/types';

let timeInterval: NodeJS.Timeout;

const useApplicationTopInfo = ({
  applicationId,

  clearErrors,
  questionnarieClearErrors,
  isCorporate,
  isCompanyOfficial,
  fields,
  questionnarieFields,
  onSaveAccountsCurrencies,
  onSaveAccountsFlows,
  onSaveExpectedFundsOrigins,
  onSaveCrsAndFatca,
  onSaveAffilatedCompany,
  setHaveSecondPassport,
  haveSecondPassport,
  setError,
  questionnarieSetError,
  changeLogs,
  activePermissions,
  currentEmployee,
  newApplicationInfo,
  amlUpdateStatus,
  riskScore,
  setSuccessMessageVisible,
  riskScoreRanges,
  setStatusChangeError,
  getApplicationDocuments,
}: {
  applicationId?: string;
  clearErrors: UseFormClearErrors<any>;
  questionnarieClearErrors: UseFormClearErrors<any>;
  isCorporate?: boolean;
  isCompanyOfficial?: boolean;
  fields?: FieldValues;
  questionnarieFields?: FieldValues;
  onSaveAccountsCurrencies?: () => void;
  onSaveAccountsFlows?: () => void;
  onSaveExpectedFundsOrigins?: () => void;
  onSaveCrsAndFatca?: () => void;
  onSaveAffilatedCompany?: () => void;
  setHaveSecondPassport?: (data: boolean) => void;
  haveSecondPassport?: boolean;
  setError: UseFormSetError<any>;
  questionnarieSetError: UseFormSetError<any>;
  changeLogs?: ChangeLog[];
  activePermissions?: PermissionEnum[];
  currentEmployee?: Employee;
  newApplicationInfo?: NewApplicationListItem | NewApplicationCorporateInfo;
  amlUpdateStatus?: CustomerScoreUpdateStatus;
  riskScore?: CustomerScore;
  setSuccessMessageVisible: (data: boolean) => void;
  riskScoreRanges?: RiskScoreRange[];
  setStatusChangeError: (data: string) => void;
  getApplicationDocuments: (id: string) => void;
}) => {
  const [rejectNotifyPopupVisible, setRejectNotifyPopupVisible] =
    useState(false);
  const [textareaIsVisible, setTextareaIsVisible] = useState(false);
  const [responsiblePopupVisible, setResponsiblePopupVisible] = useState(false);
  const [selectedResponsible, setSelectedResponsible] = useState('');
  const [withStatusChange, setWithStatusChange] = useState(false);
  const [actionMenuVisible, setActionMenuVisibile] = useState(false);
  const [duplicatesCollapsed, setDuplicatesCollapsed] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [questionnarieGlobalError, setQuestionnarieGlobalError] = useState('');
  const [changeLogsPanelIsOpen, setChangeLogsPanelIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'system' | 'change'>('system');
  const [userSidepanelVisible, setUserSidepanelVisible] = useState(false);
  const [resetPopupUpVisible, setResetPopupVisible] = useState(false);
  const [token, setToken] = useState('');
  const [resetCopyIsSuccess, setResetCopyIsSuccess] = useState(false);
  const [idCopySuccess, setIdCopySuccess] = useState(false);
  const [userDetailsActiveTab, setUserDetailsActiveTab] = useState<
    'general' | 'locks' | 'logs'
  >('general');
  const [lockId, setLockId] = useState('');
  const [lockFormVisible, setLockFormVisible] = useState(false);
  const [activeStatus, setActiveStatus] = useState('');
  const [totalRiskScore, setTotalRiskScore] = useState(0);
  const [totalRiskScoreLevel, setTotalRiskScoreLevel] = useState('');
  const [manualScoreSidepanelVisible, setManualScoreSidepanelVisible] =
    useState(false);
  const [manualRiskScoreError, setManualRiskScoreError] = useState('');
  const [
    riskScoreChangeLogsSidepanelVisible,
    setRiskScoreChangeLogsSidepanelVisible,
  ] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);

  const dispatch = useAppDispatch();

  const prevApplicationId = usePrevious(applicationId);

  const [updateInfo, { isLoading: udpateInProgress }] =
    useUpdateNewApplicationMutation();
  const [updateCOFInfo, { isLoading: udpateCofInProgress }] =
    useUpdateCompanyOfficialApplicationMutation();
  const [updateCorporateInfo, { isLoading: udpateCorporateInProgress }] =
    useUpdateCorporateApplicationMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetApplicationChangeLogsQuery();
  const [
    updateCOFQuestionnarie,
    { isLoading: updateCofQuestionnarieInProgress },
  ] = useUpdateCOFQuestionnarieMutation();
  const [
    updateIndividualQuestionnarie,
    { isLoading: updateIndividualQuestionnarieInProgress },
  ] = useUpdateInividualQuestionnarieMutation();
  const [
    updateCorporateQuestionnarie,
    { isLoading: updateCorporateQuestionnarieInProgress },
  ] = useUpdateCorporateQuestionnarieMutation();
  const [onSubmitApplication, { isLoading: submitIsLoading }] =
    useSubmitApplicationMutation();
  const [resetCustomerPassword, { isLoading: recoveryInProgress }] =
    useResetApplicationClientPasswordMutation();
  const [
    getListAuditLogs,
    { isFetching: auditLogsIsFetching, isLoading: auditLogsIsLoading },
  ] = useLazyGetApplicationClientAuditLogsQuery();
  const [getLocks, { isLoading: locksIsLoading, isFetching: locksIsFetching }] =
    useLazyGetApplicationClientLocksQuery();
  const [
    getLockItem,
    { isLoading: lockItemIsLoading, isFetching: lockItemIsFetching },
  ] = useLazyGetApplicationClientLockItemQuery();
  const [create, { isLoading: createLockRequestInProgress, isSuccess }] =
    useCreateApplicationClientLockMutation();
  const [
    update,
    { isLoading: updateInProgress, isSuccess: updateLockIsSuccess },
  ] = useUpdateApplicationClientLockMutation();
  const [
    getLockItemLogsList,
    { isFetching: lockItemLogsIsFetching, isLoading: lockItemLogsIsLoading },
  ] = useLazyGetApplicationClientLockItemAuditLogsQuery();
  const [getClientActiveLock] = useLazyGetApplicationClientActiveLocksQuery();
  const [updateClient, { isLoading: updateClientInProgress }] =
    useUpdateApplicationClientMutation();
  const [
    createApplicationManualRiskScore,
    {
      isLoading: createRiskScoreIsLoading,
      isSuccess: createRiskScoreIsSuccess,
    },
  ] = useCreateApplicationManualRiskScoreMutation();
  const [
    getApplicationsAmlScoresHistory,
    {
      isLoading: amlScoresHistoryIsLoading,
      isFetching: amlScoresHistoryIsFetching,
    },
  ] = useLazyGetApplicationsAMLScoresHistoryQuery();
  const [
    setReviewEmployee,
    {
      isLoading: setReviewEmployeeInProgress,
      isSuccess: setReviewEmployeeIsSuccess,
    },
  ] = useSetReviewEmployeeMutation();
  const [getApplicationRiskScore, { isLoading: riskScoreIsLoading }] =
    useLazyGetApplicationRiskScoreQuery();
  const [getApplicationAmlUpdatingItem] =
    useLazyGetApplicationAmlUpdatingItemQuery();
  const [
    createApplicationScore,
    {
      isSuccess: riskScoreRequestIsSuccess,
      isLoading: riskScoreRequestInProgress,
    },
  ] = useCreateApplicationRiskScoreMutation();
  const [backToIncomplete, { isLoading: backToIncompleteInProgress }] =
    useUpdateIndividualApplicationStatusMutation();
  const [
    rejectApplication,
    {
      isLoading: rejectInProgress,
      isSuccess: rejectIsSuccess,
      isError: rejectIsError,
    },
  ] = useUpdateIndividualApplicationStatusMutation();
  const [
    addResponsibleToApplication,
    {
      isLoading: addResponsibleInProgress,
      isSuccess: addResponsibleIsSuccess,
      isError: addResponsibleIsError,
    },
  ] = useUpdateIndividualApplicationStatusMutation();
  const [
    rejectCorporateApplication,
    {
      isLoading: corporateRejectInProgress,
      isSuccess: corporateRejectIsSuccess,
      isError: corporateRejectIsError,
    },
  ] = useUpdateCorporateApplicationStatusMutation();
  const [
    addCorporateResponsibleToApplication,
    {
      isLoading: addCorporateResponsibleInProgress,
      isSuccess: addCorporateResponsibleIsSuccess,
      isError: addCorporateResponsibleIsError,
    },
  ] = useUpdateCorporateApplicationStatusMutation();
  const [
    corporateBackToIncomplete,
    { isLoading: corporateBackToIncompleteInProgress },
  ] = useUpdateCorporateApplicationStatusMutation();
  const [
    addCOFResponsibleToApplication,
    {
      isLoading: addCOFResponsibleInProgress,
      isSuccess: addCOFResponsibleIsSuccess,
      isError: addCOFResponsibleIsError,
    },
  ] = useUpdateCompanyOfficialStatusMutation();
  const [
    rejectCompanyOfficialApplication,
    {
      isLoading: rejectCOFInProgress,
      isSuccess: rejectCOFIsSuccess,
      isError: rejectCOFIsError,
    },
  ] = useUpdateCompanyOfficialStatusMutation();
  const [cofBackToIncomplete, { isLoading: cofBackToIncompleteInProgress }] =
    useUpdateCompanyOfficialStatusMutation();
  const [getSystemLogs] = useLazyGetNewApplicationSystemLogsQuery();
  const [
    addComment,
    { isLoading: sendCommentInProgress, isSuccess: sendCommentIsSuccess },
  ] = useAddNewApplicationCommentMutation();

  const addCommentAction = useCallback(
    (text: string) => {
      addComment({
        id: applicationId as string,
        body: {
          text,
        },
      });
    },
    [applicationId],
  );

  const getSystemLogsAction = useCallback(() => {
    if (applicationId) {
      getSystemLogs(applicationId);
    }
  }, [applicationId]);

  const onAddResponsible = async (data: {
    status: NewApplicationStatus;
    id?: string | undefined;
  }) => {
    setStatusChangeError('');

    let response;

    if (isCorporate) {
      response = addCorporateResponsibleToApplication({
        id: applicationId as string,
        body: {
          status: data.status,
          reviewEmployeeId: data.id,
        },
      }) as { error?: { data?: { message?: string } } };
    } else if (isCompanyOfficial) {
      response = addCOFResponsibleToApplication({
        id: applicationId as string,
        body: {
          status: data.status,
          reviewEmployeeId: data.id,
        },
      }) as { error?: { data?: { message?: string } } };
    } else {
      response = addResponsibleToApplication({
        id: applicationId as string,
        body: {
          status: data.status,
          reviewEmployeeId: data.id,
        },
      }) as { error?: { data?: { message?: string } } };
    }

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const onRejectAplication = async () => {
    setStatusChangeError('');

    let response;

    if (isCorporate) {
      response = (await rejectCorporateApplication({
        id: applicationId as string,
        body: { status: NewApplicationStatus.DECLINED },
      })) as { error?: { data?: { message?: string } } };
    } else if (isCompanyOfficial) {
      response = (await rejectCompanyOfficialApplication({
        id: applicationId as string,
        body: { status: NewApplicationStatus.DECLINED },
      })) as { error?: { data?: { message?: string } } };
    } else {
      response = (await rejectApplication({
        id: applicationId as string,
        body: { status: NewApplicationStatus.DECLINED },
      })) as { error?: { data?: { message?: string } } };
    }

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const onBackToIncomplete = async () => {
    setStatusChangeError('');

    let response;

    if (isCorporate) {
      response = (await corporateBackToIncomplete({
        id: applicationId as string,
        body: { status: NewApplicationStatus.INCOMPLETE },
      })) as { error?: { data?: { message?: string } } };
    } else if (isCompanyOfficial) {
      response = (await cofBackToIncomplete({
        id: applicationId as string,
        body: { status: NewApplicationStatus.INCOMPLETE },
      })) as { error?: { data?: { message?: string } } };
    } else {
      response = (await backToIncomplete({
        id: applicationId as string,
        body: { status: NewApplicationStatus.INCOMPLETE },
      })) as { error?: { data?: { message?: string } } };
    }

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const onUpdate = useCallback(() => {
    createApplicationScore(applicationId as string);
  }, [applicationId]);

  const getApplicationRiskScoreAction = useCallback(async () => {
    (await getApplicationRiskScore(applicationId as string)) as {
      data: { data: CustomerScore };
    };
  }, [applicationId]);

  const getApplicationLasCalculaingScoreAction = useCallback(
    async (amlUpdateId: string) => {
      const { data } = await getApplicationAmlUpdatingItem({
        orgId: applicationId as string,
        id: amlUpdateId,
      });

      if (
        data?.data.status === CustomerScoreUpdateStatus.FAIL ||
        data?.data.status === CustomerScoreUpdateStatus.SUCCESS
      ) {
        clearInterval(timeInterval);
      }

      if (data?.data.status === CustomerScoreUpdateStatus.SUCCESS) {
        getApplicationRiskScore(applicationId as string);
      }
    },
    [],
  );

  const setReviewEmployeeAction = () => {
    setReviewEmployee({
      id: applicationId as string,
      body: {
        reviewEmployeeId: selectedResponsible,
      },
    });
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=APPLICATION&entityId=${applicationId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [applicationId],
  );

  const getApplicationsAmlScoresHistoryAction = async (cursor?: string) => {
    await getApplicationsAmlScoresHistory({
      id: applicationId as string,
      query: `?take=40${cursor ? `&cursor=${cursor}` : ''}`,
    });
  };

  const onResetPassword = async () => {
    const response = (await resetCustomerPassword(
      newApplicationInfo?.user?.id as string,
    )) as {
      data: { data: { token: string } };
    };

    if (response && response.data && response.data.data) {
      setToken(response.data.data.token);
      setResetPopupVisible(true);
    }
  };

  const createApplicationManualRiskScoreAction = async () => {
    setManualRiskScoreError('');

    const response = (await createApplicationManualRiskScore({
      id: applicationId as string,
      totalRiskScore,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setManualRiskScoreError,
      });
    }
  };

  const onUpdateClientStatus = (clientStatus: 'ACTIVE' | 'DISABLED') => {
    setActiveStatus(clientStatus);

    updateClient({
      id: newApplicationInfo?.user?.id as string,
      body: {
        status: clientStatus,
      },
    });
  };

  const onIDCopy = (id: string) => {
    copy(id);

    setIdCopySuccess(true);

    setTimeout(() => {
      setIdCopySuccess(false);
    }, 2000);
  };

  const onSubmitApplicationAction = async () => {
    clearErrors([]);
    questionnarieClearErrors([]);
    setSubmitError('');
    setGlobalError('');
    setQuestionnarieGlobalError('');

    let updateResponse: any;

    if (!isCorporate && !isCompanyOfficial) {
      const body = getIndividualApplicationBody(
        fields as any,
        (newApplicationInfo as NewApplicationListItem)
          ?.basePersonalInfoEditable,
      );

      const updateBody = {
        ...body,
        participatesInPolitics: Boolean(
          fields?.participatesInPolitics === 'yes',
        ),
        isUSResident: Boolean(fields?.isUSResident === 'yes'),
      };

      updateResponse = await updateInfo({
        body: updateBody,
        id: applicationId as string,
      });

      if (
        newApplicationInfo?.applicationScheme &&
        (newApplicationInfo?.applicationScheme?.civilActive ||
          newApplicationInfo?.applicationScheme?.criminalOfAnyActive ||
          newApplicationInfo?.applicationScheme
            ?.lastThreeYearsAddressHistoryActive ||
          newApplicationInfo?.applicationScheme
            ?.nationalInsuranceNumberOfDirectorsActive ||
          newApplicationInfo?.applicationScheme?.regulatoryActive ||
          newApplicationInfo?.applicationScheme?.businessAndEmploymentActive ||
          newApplicationInfo?.applicationScheme?.id ===
            'ed560559-8354-4634-b6dd-eb341dfb551a')
      ) {
        const questionnarieBody = getCofandIndividualQuestionnarieBody({
          questionnarieValues:
            questionnarieFields as COFAndIndividualQuestionnarieFormValues,
          scheme: newApplicationInfo?.applicationScheme,
        });

        updateIndividualQuestionnarie({
          id: applicationId as string,
          body: questionnarieBody,
        });

        if (
          newApplicationInfo?.applicationScheme?.id ===
            'ed560559-8354-4634-b6dd-eb341dfb551a' &&
          onSaveAccountsCurrencies &&
          onSaveAccountsFlows &&
          onSaveExpectedFundsOrigins &&
          onSaveCrsAndFatca
        ) {
          onSaveAccountsCurrencies();
          onSaveAccountsFlows();
          onSaveExpectedFundsOrigins();
          onSaveCrsAndFatca();
        }
      }
    } else if (isCompanyOfficial) {
      const body: {
        firstName?: string;
        lastName?: string;
        nationalityId?: string;
        email?: string;
        dob?: string;
        deliveryChannelId?: string;
        taxNumber?: string;
      } = {};
      if (fields?.nationalityId) {
        body.nationalityId = fields.nationalityId;
      }

      if (fields?.taxNumber) {
        body.taxNumber = fields.taxNumber;
      }

      if (fields?.deliveryChannelId) {
        body.deliveryChannelId = fields.deliveryChannelId;
      }

      const updateBody = {
        ...body,
        participatesInPolitics: Boolean(
          fields?.participatesInPolitics === 'yes',
        ),
        isUSResident: Boolean(fields?.isUSResident === 'yes'),
      };

      updateResponse = await updateCOFInfo({
        body: updateBody,
        id: applicationId as string,
      });

      if (
        newApplicationInfo?.applicationScheme &&
        (newApplicationInfo?.applicationScheme?.civilActive ||
          newApplicationInfo?.applicationScheme?.criminalOfAnyActive ||
          newApplicationInfo?.applicationScheme
            ?.lastThreeYearsAddressHistoryActive ||
          newApplicationInfo?.applicationScheme
            ?.nationalInsuranceNumberOfDirectorsActive ||
          newApplicationInfo?.applicationScheme?.regulatoryActive ||
          newApplicationInfo?.applicationScheme?.businessAndEmploymentActive ||
          newApplicationInfo?.applicationScheme?.id ===
            'a87db4b4-3c78-4564-bba1-c16a19553159')
      ) {
        const questionnarieBody = getCofandIndividualQuestionnarieBody({
          questionnarieValues:
            questionnarieFields as COFAndIndividualQuestionnarieFormValues,
          scheme: newApplicationInfo?.applicationScheme,
        });

        updateCOFQuestionnarie({
          id: applicationId as string,
          body: questionnarieBody,
        });

        if (
          newApplicationInfo?.applicationScheme?.id ===
            'a87db4b4-3c78-4564-bba1-c16a19553159' &&
          onSaveAffilatedCompany &&
          onSaveCrsAndFatca
        ) {
          onSaveAffilatedCompany();
          onSaveCrsAndFatca();
        }
      }
    } else if (isCorporate) {
      const body = getCorporateAppicationBody(fields as any);

      if (!body.websiteLink) {
        body.websiteLink = '';
      }

      const updateBody = body;

      updateResponse = await updateCorporateInfo({
        body: updateBody,
        id: applicationId as string,
      });

      if (
        newApplicationInfo?.applicationScheme &&
        (newApplicationInfo?.applicationScheme?.accountantDetailsActive ||
          newApplicationInfo?.applicationScheme
            ?.auditedByExternalComplianceActive ||
          newApplicationInfo?.applicationScheme
            ?.changesInManagementOrOwnershipActive ||
          newApplicationInfo?.applicationScheme
            ?.complianceConsultantDetailsActive ||
          newApplicationInfo?.applicationScheme?.fcaNumberActive ||
          newApplicationInfo?.applicationScheme?.hmrcMlrNumberActive ||
          newApplicationInfo?.applicationScheme
            ?.hmrcRegistrationExpiryDateActive ||
          newApplicationInfo?.applicationScheme?.icoNumberActive ||
          newApplicationInfo?.applicationScheme?.itProviderDetailsActive ||
          newApplicationInfo?.applicationScheme?.mlroDetailsActive ||
          newApplicationInfo?.applicationScheme?.numberOfAgentsActive ||
          newApplicationInfo?.applicationScheme?.numberOfBranchesActive ||
          newApplicationInfo?.applicationScheme
            ?.onboardCorporateClientsActive ||
          newApplicationInfo?.applicationScheme?.onboardMsbActive ||
          newApplicationInfo?.applicationScheme?.paymentsForCharitiesActive ||
          newApplicationInfo?.applicationScheme?.payoutPartnerDetailsActive ||
          newApplicationInfo?.applicationScheme
            ?.regulatoryInvestigationActive ||
          newApplicationInfo?.applicationScheme?.screeningActive ||
          newApplicationInfo?.applicationScheme?.transactionMonitoringActive ||
          newApplicationInfo?.applicationScheme?.typeOfBusinessLicenceActive ||
          newApplicationInfo?.applicationScheme?.visitedByAuthoritiesActive ||
          newApplicationInfo?.applicationScheme
            ?.workingWithAnyOtherIPSPActive ||
          newApplicationInfo?.applicationScheme?.id ===
            'cdbb8c71-90e6-4a5e-964f-e55169e0c4df')
      ) {
        const questionnarieBody = getCorporateQuestionnarieBody({
          questionnarieValues:
            questionnarieFields as CorporateQuestionnarieFormValues,
          scheme: newApplicationInfo?.applicationScheme,
        });

        updateCorporateQuestionnarie({
          id: applicationId as string,
          body: questionnarieBody,
        });

        if (
          newApplicationInfo?.applicationScheme?.id ===
            'cdbb8c71-90e6-4a5e-964f-e55169e0c4df' &&
          onSaveAccountsCurrencies &&
          onSaveAccountsFlows &&
          onSaveExpectedFundsOrigins
        ) {
          onSaveAccountsCurrencies();
          onSaveAccountsFlows();
          onSaveExpectedFundsOrigins();
        }
      }
    }

    if (updateResponse) {
      const response = (await onSubmitApplication(applicationId as string)) as {
        error?: ResponseError;
      };

      if (
        response?.error?.data?.message &&
        Array.isArray(response?.error?.data?.message)
      ) {
        if (
          (response?.error?.data?.message?.find(
            (item) => item.property === 'secondPassportIssueCountryId',
          ) ||
            response?.error?.data?.message?.find(
              (item) => item.property === 'secondPassportNumber',
            ) ||
            response?.error?.data?.message?.find(
              (item) => item.property === 'secondPassportIssueDate',
            )) &&
          setHaveSecondPassport &&
          !haveSecondPassport
        ) {
          setHaveSecondPassport(true);
        }
      }

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError,
        });

        responseErrorsResolver({
          errors: response.error,
          setError: questionnarieSetError,
          setGlobalError: setQuestionnarieGlobalError,
        });

        setSubmitError('There are some errors in application');
      }
    }
  };

  const handleCloseAmlChangeLogsPanel = () => {
    setRiskScoreChangeLogsSidepanelVisible(false);
    dispatch(clearAmlChangeLogs());
  };

  let link: string;

  if (process.env.CUSTOMERS_HOST) {
    link = process.env.CUSTOMERS_HOST;
  } else {
    link = window.location.host.includes('baseella')
      ? window.location.host.replace('backoffice', 'customers')
      : '';
  }

  const onResetCopy = () => {
    copy(`${link}/auth/reset-password?token=${token}`);

    setResetCopyIsSuccess(true);

    setTimeout(() => {
      setResetCopyIsSuccess(false);
    }, 2000);
  };

  useEffect(() => {
    if (sendCommentIsSuccess) {
      setTextareaIsVisible(false);
    }
  }, [sendCommentIsSuccess]);

  useEffect(() => {
    if (
      changeLogsPanelIsOpen &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [changeLogsPanelIsOpen]);

  useEffect(() => {
    if (
      addResponsibleIsSuccess ||
      addCorporateResponsibleIsSuccess ||
      addCOFResponsibleIsSuccess ||
      (!withStatusChange && setReviewEmployeeIsSuccess)
    ) {
      setResponsiblePopupVisible(false);
      setSelectedResponsible('');
      setWithStatusChange(false);
    }
  }, [
    addResponsibleIsSuccess,
    setReviewEmployeeIsSuccess,
    withStatusChange,
    addCorporateResponsibleIsSuccess,
    addCOFResponsibleIsSuccess,
  ]);

  useEffect(() => {
    if (
      !newApplicationInfo?.reviewEmployee &&
      !selectedResponsible &&
      responsiblePopupVisible &&
      currentEmployee
    ) {
      setSelectedResponsible(currentEmployee.id);
    }
  }, [
    responsiblePopupVisible,
    selectedResponsible,
    currentEmployee,
    newApplicationInfo?.reviewEmployee,
  ]);

  useEffect(() => {
    if (newApplicationInfo || createRiskScoreIsSuccess) {
      setManualRiskScoreError('');
      setTotalRiskScore(0);
      setTotalRiskScoreLevel('');
      setManualScoreSidepanelVisible(false);

      if (
        activePermissions?.includes(
          PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
        )
      ) {
        getApplicationRiskScoreAction();
      }
    }
  }, [createRiskScoreIsSuccess, getApplicationRiskScore, newApplicationInfo]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
      )
    ) {
      const amlUpdateId = localStorage.getItem('applicationAmlUpdateId');

      if (amlUpdateId) {
        timeInterval = setInterval(() => {
          getApplicationLasCalculaingScoreAction(amlUpdateId);
        }, 15000);
      }
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if ((!newApplicationInfo && applicationId) || !prevApplicationId) {
      getSystemLogsAction();
    }
  }, [applicationId, newApplicationInfo, prevApplicationId]);

  useEffect(() => {
    if (isSuccess && riskScore) {
      setSuccessMessageVisible(true);

      setTimeout(() => {
        setSuccessMessageVisible(false);
      }, 6000);
    }
  }, [isSuccess]);

  useEffect(
    () => () => {
      clearInterval(timeInterval);
    },
    [],
  );

  useEffect(() => {
    if (
      newApplicationInfo?.reviewedDuplicates &&
      newApplicationInfo?.reviewedDuplicates?.length > 0 &&
      newApplicationInfo?.reviewStatus !== ReviewStatus.HAS_DUPLICATE
    ) {
      setDuplicatesCollapsed(true);
    }
  }, [
    newApplicationInfo?.reviewStatus,
    newApplicationInfo?.reviewedDuplicates,
  ]);

  useEffect(() => {
    if (
      newApplicationInfo?.reviewEmployee &&
      !selectedResponsible &&
      responsiblePopupVisible
    ) {
      setSelectedResponsible(newApplicationInfo?.reviewEmployee.id);
    }
  }, [
    newApplicationInfo?.reviewEmployee,
    selectedResponsible,
    responsiblePopupVisible,
  ]);

  useEffect(() => {
    if (userSidepanelVisible && newApplicationInfo?.user) {
      getClientActiveLock(newApplicationInfo?.user?.id);
    }
  }, [userSidepanelVisible, newApplicationInfo?.user]);

  useEffect(() => {
    if (isSuccess || updateLockIsSuccess) {
      getClientActiveLock(newApplicationInfo?.user?.id as string);
    }
  }, [isSuccess, updateLockIsSuccess]);

  useEffect(() => {
    if (newApplicationInfo?.user) {
      setActiveStatus(newApplicationInfo?.user?.status);
    }
  }, [newApplicationInfo?.user]);

  useEffect(() => {
    if (riskScore && riskScoreRanges && manualScoreSidepanelVisible) {
      setTotalRiskScore(riskScore?.totalRiskScore);

      setTotalRiskScoreLevel(
        riskScoreRanges?.find(
          (item) =>
            riskScore?.totalRiskScore >= item.rangeStart &&
            riskScore?.totalRiskScore <= item.rangeEnd,
        )?.id as string,
      );
    }
  }, [riskScore, riskScoreRanges, manualScoreSidepanelVisible]);

  useEffect(() => {
    if (
      (addResponsibleIsSuccess ||
        addResponsibleIsError ||
        addCorporateResponsibleIsError ||
        addCOFResponsibleIsError) &&
      applicationId
    ) {
      getApplicationDocuments(applicationId);
    }
  }, [
    addResponsibleIsSuccess,
    applicationId,
    addResponsibleIsError,
    addCorporateResponsibleIsError,
    addCOFResponsibleIsError,
  ]);

  useEffect(() => {
    if (
      rejectIsSuccess ||
      rejectIsError ||
      corporateRejectIsError ||
      corporateRejectIsSuccess ||
      rejectCOFIsError ||
      rejectCOFIsSuccess
    ) {
      setPopupVisible(false);
    }
  }, [
    rejectIsSuccess,
    rejectIsError,
    corporateRejectIsError,
    corporateRejectIsSuccess,
    rejectCOFIsError,
    rejectCOFIsSuccess,
  ]);

  return {
    setActionMenuVisibile,
    actionMenuVisible,
    submitIsLoading,
    udpateInProgress,
    udpateCofInProgress,
    udpateCorporateInProgress,
    updateCofQuestionnarieInProgress,
    updateIndividualQuestionnarieInProgress,
    updateCorporateQuestionnarieInProgress,
    onSubmitApplicationAction,
    setRejectNotifyPopupVisible,
    submitError,
    globalError,
    questionnarieGlobalError,
    setResponsiblePopupVisible,
    setWithStatusChange,
    setUserSidepanelVisible,
    setChangeLogsPanelIsOpen,
    onIDCopy,
    idCopySuccess,
    riskScoreRequestIsSuccess,
    riskScoreRequestInProgress,
    riskScoreIsLoading,
    onUpdate,
    setRiskScoreChangeLogsSidepanelVisible,
    setManualScoreSidepanelVisible,
    duplicatesCollapsed,
    setDuplicatesCollapsed,
    textareaIsVisible,
    setTextareaIsVisible,
    rejectNotifyPopupVisible,
    withStatusChange,
    setReviewEmployeeAction,
    selectedResponsible,
    setReviewEmployeeInProgress,
    responsiblePopupVisible,
    setSelectedResponsible,
    changeLogsPanelIsOpen,
    activeTab,
    setActiveTab,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
    userSidepanelVisible,
    setUserDetailsActiveTab,
    userDetailsActiveTab,
    getLocks,
    activeStatus,
    updateClientInProgress,
    onUpdateClientStatus,
    recoveryInProgress,
    onResetPassword,
    getListAuditLogs,
    auditLogsIsFetching,
    auditLogsIsLoading,
    setLockId,
    setLockFormVisible,
    locksIsLoading,
    getLockItem,
    create,
    update,
    locksIsFetching,
    isSuccess,
    lockItemIsLoading,
    lockItemIsFetching,
    createLockRequestInProgress,
    updateInProgress,
    lockId,
    lockFormVisible,
    getLockItemLogsList,
    lockItemLogsIsLoading,
    lockItemLogsIsFetching,
    setResetPopupVisible,
    resetPopupUpVisible,
    resetCopyIsSuccess,
    link,
    token,
    onResetCopy,
    manualScoreSidepanelVisible,
    setTotalRiskScore,
    setTotalRiskScoreLevel,
    setManualRiskScoreError,
    manualRiskScoreError,
    createRiskScoreIsLoading,
    createApplicationManualRiskScoreAction,
    totalRiskScoreLevel,
    totalRiskScore,
    riskScoreChangeLogsSidepanelVisible,
    handleCloseAmlChangeLogsPanel,
    getApplicationsAmlScoresHistoryAction,
    amlScoresHistoryIsLoading,
    amlScoresHistoryIsFetching,
    backToIncompleteInProgress:
      backToIncompleteInProgress ||
      corporateBackToIncompleteInProgress ||
      cofBackToIncompleteInProgress,
    setPopupVisible,
    onBackToIncomplete,
    onAddResponsible,
    addResponsibleInProgress:
      addResponsibleInProgress ||
      addCorporateResponsibleInProgress ||
      addCOFResponsibleInProgress,
    onRejectAplication,
    rejectInProgress:
      rejectInProgress || corporateRejectInProgress || rejectCOFInProgress,
    popupVisible,
    addCommentAction,
    sendCommentInProgress,
  };
};

export default useApplicationTopInfo;
